/**
 * Plan cards shown on the billing surfaces (Plan & Usage, Compare Plans).
 *
 * One card: Enterprise. Every client is on it; what they get (credits,
 * features, prices) is agreed per deal and set in the backoffice, so the
 * customer billing UI no longer sells tiers (`UpgradeModal` and
 * `TrialAudienceUpgradeModal` still read this card).
 *
 * Numbers come from `@digital-twins/shared-types` (`pricing-constants.ts`),
 * which the backend mirrors in `apps/backend/src/billing/billing-config.service.ts`
 * (`HARDCODED_DEFAULTS`) and in the `pricing_plans.settings` rows seeded via the
 * launch-pricing migrations.
 *
 * The backend currently does NOT expose a public `GET /billing/plans` endpoint
 * that returns this data; until it does, everything hardcoded below (labels,
 * copy) MUST be kept in sync with the backend by hand. Any
 * price change in Lemon Squeezy that is mirrored to `pricing_plans.settings`
 * must also be reflected here. Tracked as a follow-up in
 * `docs/plans/launch-pricing.md` (Wave U1).
 */

import type { SubscriptionTier } from '@/lib/api'
import { PLAN_CREDITS, OVERAGE_RATE_CENTS_MONTHLY } from '@digital-twins/shared-types'

export type SubscribablePlan = Extract<SubscriptionTier, 'pro' | 'enterprise'>

/** Card identity. */
export type PlanCardId = 'enterprise'

export interface PlanCardConfig {
  id: PlanCardId
  /** Tier this card actually sells. `null` = marketing-only card. */
  tier: SubscribablePlan | null
  displayName: string
  logoSrc: string
  /** Price in dollars per month. `null` renders as "Custom". */
  monthlyPriceUsd: number | null
  /** Line under the price, e.g. 'billed annually'. Empty string renders nothing. */
  priceCaption: string
  /** Pre-formatted credits line, ready to render as-is. */
  creditsLabel: string
  /** Credits included per period. `null` when the card advertises no figure. */
  includedCredits: number | null
  /** Per-credit overage in cents. `null` when the card advertises no figure. */
  overageRateCentsMonthly: number | null
  shortDescription: string
  showUnlimitedSeatsPill: boolean
  /** True for cards whose CTA is "Contact sales" rather than a checkout. */
  contactSales: boolean
  /**
   * Where the "Contact sales" CTA sends the user. Defaults to the public
   * Fairgen contact form. Open in a new tab so the user doesn't lose their
   * in-app session / cart state.
   */
  contactSalesUrl?: string
}

/**
 * Public contact form. Used by every "Contact sales" CTA across billing
 * surfaces; centralized here so a future URL change (e.g. typeform, custom
 * Enterprise inquiry path) is a one-line edit.
 */
export const CONTACT_SALES_URL = 'https://www.fairgen.ai/forms/contact-us'

/**
 * Enterprise is custom priced on buying surfaces (`monthlyPriceUsd: null`
 * renders as "Custom"). Credits and overage still come from the shared pricing
 * constants (10,000 credits in a shared org pool, 25¢ per extra credit).
 */
export const PLAN_CARDS: PlanCardConfig[] = [
  {
    id: 'enterprise',
    tier: 'enterprise',
    displayName: 'Enterprise',
    logoSrc: '/billing/logo-plan-enterprise.svg',
    monthlyPriceUsd: null,
    priceCaption: '',
    creditsLabel: `${PLAN_CREDITS.enterprise.toLocaleString()} credits / org`,
    includedCredits: PLAN_CREDITS.enterprise,
    overageRateCentsMonthly: OVERAGE_RATE_CENTS_MONTHLY.enterprise,
    shortDescription: 'Shared credit pool · Unlimited seats · Priority support',
    showUnlimitedSeatsPill: true,
    contactSales: false,
  },
]

export function getPlanCard(id: PlanCardId): PlanCardConfig {
  const card = PLAN_CARDS.find((c) => c.id === id)
  if (!card) {
    throw new Error(`Unknown plan card: ${id}`)
  }
  return card
}

/** Seat count that reads as "unlimited" (mirrors backend `HARDCODED_DEFAULTS.maxSeats`). */
export const UNLIMITED_SEATS_THRESHOLD = 9999

/**
 * Seat cap for a tier. Approximation while there is no plan config endpoint; mirrors the
 * backend `HARDCODED_DEFAULTS`. Shared by the plan violation banner and the plan features page.
 */
export function approxMaxSeats(tier: SubscriptionTier | null | undefined): number {
  if (tier === 'pro' || tier === 'enterprise') return UNLIMITED_SEATS_THRESHOLD
  return 1
}
